import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Heart,
  CreditCard,
  Bank,
  DeviceMobile,
  CurrencyCircleDollar,
  Handshake,
  CheckCircle,
  ArrowRight,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const donationMethods = [
  {
    icon: DeviceMobile,
    title: "Mobile Money",
    description: "Best for local supporters across Uganda.",
    details: [
      { label: "MTN Mobile Money", value: "+256 782 565 502" },
      { label: "Airtel Money", value: "+256 782 565 502" },
    ],
    color: "amber",
  },
  {
    icon: Bank,
    title: "Bank Transfer",
    description: "Direct bank transfer for larger donations.",
    details: [
      { label: "Bank Name", value: "Absa Bank Uganda Limited" },
      { label: "Account Name", value: "PEDA AFRICA" },
      { label: "Account No", value: "Contact us for details" },
    ],
    color: "green",
  },
  {
    icon: CurrencyCircleDollar,
    title: "PayPal",
    description: "For international supporters worldwide.",
    details: [
      { label: "PayPal Link", value: "Coming soon" },
    ],
    color: "blue",
  },
  {
    icon: CreditCard,
    title: "Card Payments",
    description: "Debit and credit card donations globally.",
    details: [
      { label: "Stripe", value: "Coming soon" },
    ],
    color: "purple",
  },
];

const impactAreas = [
  {
    title: "Youth Skills Training",
    description: "Support TVET programs that equip young people with employable skills.",
    amount: "$50",
    impact: "Trains 1 youth for a week",
  },
  {
    title: "Women Empowerment",
    description: "Help women entrepreneurs start and grow their businesses.",
    amount: "$100",
    impact: "Business starter kit for 1 woman",
  },
  {
    title: "Community Enterprise",
    description: "Fund community-led business initiatives and cooperatives.",
    amount: "$500",
    impact: "Supports a community project",
  },
  {
    title: "Leadership Development",
    description: "Invest in training transformational community leaders.",
    amount: "$1,000",
    impact: "Full leadership program sponsorship",
  },
];

export default function DonatePage() {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-green-700 to-green-900" data-testid="donate-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 pattern-dots" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="h-10 w-10 text-amber-400" weight="fill" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6">
              Support Our Mission
            </h1>
            <p className="text-lg text-green-100/90 leading-relaxed mb-8">
              Your support helps PEDA AFRICA empower communities, promote entrepreneurship, and create sustainable livelihoods across Africa. Every contribution makes a difference.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg" 
                className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-8"
                data-testid="donate-now-button"
              >
                <a href="#donation-methods">
                  Donate Now <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
                </a>
              </Button>
              <Button 
                asChild 
                size="lg" 
                variant="outline" 
                className="bg-transparent border-white/30 text-white hover:bg-white/10 rounded-xl px-8"
                data-testid="become-partner-button"
              >
                <Link to="/partnerships">Become a Partner</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="py-20 md:py-28" data-testid="donate-impact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Your Impact
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Where Your Donation Goes
            </h2>
            <p className="text-lg text-slate-600">
              See how your contribution directly impacts communities across Africa.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {impactAreas.map((area, index) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-all group"
              >
                <div className="text-3xl font-bold text-green-700 mb-2">{area.amount}</div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{area.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">{area.description}</p>
                <div className="pt-4 border-t border-slate-100">
                  <p className="text-xs font-semibold text-amber-600">{area.impact}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Methods */}
      <section id="donation-methods" className="py-20 md:py-28 bg-slate-50" data-testid="donation-methods-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Giving
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Donation Methods
            </h2>
            <p className="text-lg text-slate-600">
              Support PEDA through secure and convenient giving channels.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {donationMethods.map((method, index) => (
              <motion.div
                key={method.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  method.color === "amber" ? "bg-amber-100" :
                  method.color === "green" ? "bg-green-100" :
                  method.color === "blue" ? "bg-blue-100" : "bg-purple-100"
                }`}>
                  <method.icon className={`h-6 w-6 ${
                    method.color === "amber" ? "text-amber-600" :
                    method.color === "green" ? "text-green-700" :
                    method.color === "blue" ? "text-blue-600" : "text-purple-600"
                  }`} weight="duotone" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{method.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">{method.description}</p>
                
                <div className="space-y-2">
                  {method.details.map((detail) => (
                    <div key={detail.label} className="text-sm">
                      <span className="font-semibold text-slate-700">{detail.label}:</span>{" "}
                      <span className="text-slate-500">{detail.value}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 rounded-3xl border border-green-100 bg-green-50 p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Payment Details Coming Soon</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We are setting up secure payment channels to receive your generous donations. In the meantime, you can contact us directly to arrange your contribution or discuss partnership opportunities.
            </p>
            <Button 
              asChild 
              className="bg-green-700 hover:bg-green-800 rounded-xl px-6"
              data-testid="contact-to-donate-button"
            >
              <Link to="/contact">
                Contact Us to Donate <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Other Ways to Support */}
      <section className="py-20 md:py-28" data-testid="other-support-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
                Beyond Donations
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
                Other Ways to Support
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                There are many ways to support our mission beyond financial contributions. Your time, skills, and networks can make a significant impact.
              </p>

              <div className="space-y-4">
                {[
                  { title: "Volunteer", description: "Share your skills and expertise with our programs" },
                  { title: "Partner", description: "Collaborate with us on community development projects" },
                  { title: "Sponsor", description: "Fund specific programs or events" },
                  { title: "Advocate", description: "Spread awareness about our work in your network" },
                  { title: "In-Kind Donations", description: "Donate equipment, materials, or services" },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-700 shrink-0 mt-0.5" weight="fill" />
                    <div>
                      <p className="font-semibold text-slate-900">{item.title}</p>
                      <p className="text-sm text-slate-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-3xl p-8 text-green-950">
              <Handshake className="h-12 w-12 mb-6" weight="duotone" />
              <h3 className="text-2xl font-bold mb-4">Corporate Partnerships</h3>
              <p className="text-green-900/80 leading-relaxed mb-6">
                Partner with PEDA as part of your Corporate Social Responsibility (CSR) initiatives. We offer various partnership models that create shared value for your organization and the communities we serve.
              </p>
              <Button 
                asChild 
                className="bg-green-700 hover:bg-green-800 text-white rounded-xl px-6"
                data-testid="corporate-partnership-button"
              >
                <Link to="/partnerships">
                  Explore Partnerships <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Fundraising Projects CTA */}
      <section className="py-16 md:py-24 bg-white" data-testid="fundraising-projects">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Be Part of the Change
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Your Support Can Transform Lives
            </h2>
            <p className="text-lg text-slate-600">
              Your support can transform lives in Bombo and beyond. Choose how you want to make a lasting impact.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-10">
            {/* TVET School Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden border border-slate-100 shadow-lg"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/afobkmcc_SCHOOL.png" 
                alt="TVET School Bombo"
                className="w-full h-52 object-cover"
              />
              <div className="p-6 bg-white">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold">NEW PROJECT</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">TVET School &ndash; Bombo</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-5">
                  Help us build classrooms, provide tools and equipment, and sponsor students from low-income families to gain practical skills for employment.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild className="bg-green-700 hover:bg-green-800 text-white font-semibold rounded-xl px-5 text-sm" data-testid="donate-sponsor-student">
                    <a href="#donation-methods">Sponsor a Student</a>
                  </Button>
                  <Button asChild variant="outline" className="border-green-700 text-green-700 hover:bg-green-50 rounded-xl px-5 text-sm" data-testid="donate-fund-classroom">
                    <a href="#donation-methods">Fund a Classroom</a>
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Healthcare Center Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="rounded-2xl overflow-hidden border border-slate-100 shadow-lg"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/qkndwzxh_NR.png" 
                alt="Healthcare Center Bombo"
                className="w-full h-52 object-cover"
              />
              <div className="p-6 bg-white">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-full bg-cyan-100 text-cyan-700 text-xs font-bold">COMING SOON</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Community Healthcare Center &ndash; Bombo</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-5">
                  Help us construct and equip the facility, provide medical supplies, and support healthcare training programs for the Bombo community.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-5 text-sm" data-testid="donate-healthcare-access">
                    <a href="#donation-methods">Support Healthcare Access</a>
                  </Button>
                  <Button asChild variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50 rounded-xl px-5 text-sm" data-testid="donate-partner-peda">
                    <Link to="/partnerships">Partner With PEDA</Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-green-950" data-testid="donate-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Together, We Can Create Lasting Change
          </h2>
          <p className="text-lg text-green-100/80 mb-8 leading-relaxed">
            Donate today and make a lasting impact. Your generosity empowers communities, builds enterprises, and creates sustainable livelihoods across Africa.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-8"
              data-testid="final-donate-button"
            >
              <a href="#donation-methods">
                Donate Now <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
              </a>
            </Button>
            <Button 
              asChild 
              size="lg" 
              variant="outline"
              className="bg-transparent border-white/30 text-white hover:bg-white/10 rounded-xl px-8"
              data-testid="final-partner-button"
            >
              <Link to="/partnerships">Partner With PEDA AFRICA</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
